import React, { useCallback } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './controls.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

interface SMLSegmentedProps<T extends string> {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  disabled?: boolean;
}

interface SegmentProps<T extends string> {
  option: SegmentedOption<T>;
  isOn: boolean;
  disabled: boolean;
  onChange: (value: T) => void;
}

function SegmentComponent<T extends string>({ option, isOn, disabled, onChange }: SegmentProps<T>) {
  const pick = useCallback(() => onChange(option.value), [onChange, option.value]);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isOn}
      className={clsx(styles.segment, isOn && styles.segmentOn)}
      onClick={pick}
      disabled={disabled}
    >
      {option.label}
    </button>
  );
}

const Segment = TypedMemo(SegmentComponent);

/** A choice of 2 to 4 options (design d0: "2 to 4 options: segmented control"). */
function SMLSegmentedComponent<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: SMLSegmentedProps<T>) {
  return (
    <div className={styles.segmented} role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => (
        <Segment
          key={option.value}
          option={option}
          isOn={option.value === value}
          disabled={disabled}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

export const SMLSegmented = TypedMemo(SMLSegmentedComponent);
